/**
 * RPG action hooks (ТЗ №5, под-фаза D3).
 *
 * Thin wrappers over the entity write functions via {@link useApiMutation}, plus a shared
 * {@link rpgErrorMessage} that surfaces the backend's localized ``detail`` (insufficient
 * mora, rank too low, daily limit, cooldown, sold out, recipient not found, …). The
 * frontend is untrusted: it never computes rewards/costs/outcomes — it only triggers the
 * action and re-reads server state afterward.
 */
import { ApiError, useApiMutation, type MutationState } from "@shared/api";
import {
  postBattle,
  postBuy,
  postConvert,
  postCraft,
  postEquipWeapon,
  postTransfer,
  postUpgradeStat,
  postUpgradeWeapon,
  type RpgBattleResult,
  type RpgConvertResult,
  type RpgProfile,
  type RpgTransferInput,
  type RpgTransferResult,
  type UpgradeStat,
} from "@entities/rpg";

/** Extract a user-facing RU message from an API error (prefers the backend ``detail``). */
export function rpgErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { detail?: string } | null;
    if (payload?.detail) {
      return payload.detail;
    }
    if (err.isAuthError) {
      return "Требуется авторизация Telegram.";
    }
  }
  return "Не удалось выполнить действие. Попробуйте ещё раз.";
}

/** ``POST /api/rpg/battle/`` — fight a mob. */
export function useBattleMutation(): MutationState<RpgBattleResult, number> {
  return useApiMutation<RpgBattleResult, number>((mobId) => postBattle(mobId));
}

/** ``POST /api/rpg/weapons/equip/`` — equip an owned weapon. */
export function useEquipWeaponMutation(): MutationState<RpgProfile, number> {
  return useApiMutation<RpgProfile, number>((weaponId) => postEquipWeapon(weaponId));
}

/** ``POST /api/rpg/upgrade-weapon/`` — level a weapon for mora + materials. */
export function useUpgradeWeaponMutation(): MutationState<RpgProfile, number> {
  return useApiMutation<RpgProfile, number>((weaponId) => postUpgradeWeapon(weaponId));
}

/** ``POST /api/rpg/upgrade-stat/`` — raise hp / attack for mora. */
export function useUpgradeStatMutation(): MutationState<RpgProfile, UpgradeStat> {
  return useApiMutation<RpgProfile, UpgradeStat>((stat) => postUpgradeStat(stat));
}

/** ``POST /api/rpg/alchemy/craft/`` — craft a recipe. */
export function useCraftMutation(): MutationState<RpgProfile, number> {
  return useApiMutation<RpgProfile, number>((recipeId) => postCraft(recipeId));
}

/** ``POST /api/rpg/shop/buy/`` — buy a listing for mora. */
export function useBuyMutation(): MutationState<RpgProfile, number> {
  return useApiMutation<RpgProfile, number>((shopItemId) => postBuy(shopItemId));
}

/** ``POST /api/rpg/convert/`` — convert mora → primogems. */
export function useConvertMutation(): MutationState<RpgConvertResult, number> {
  return useApiMutation<RpgConvertResult, number>((mora) => postConvert(mora));
}

/** ``POST /api/rpg/transfer/`` — send mora / primogems to another player. */
export function useTransferMutation(): MutationState<
  RpgTransferResult,
  RpgTransferInput
> {
  return useApiMutation<RpgTransferResult, RpgTransferInput>((input) =>
    postTransfer(input),
  );
}
